import { Dialog, Transition } from '@headlessui/react'
import React, { useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ParticleLoader from "../ParticleLoader";
import 'react-datepicker/dist/react-datepicker.css';
import { transcriptionAnalysis } from '../../Requests/NewAnalysisRequests';
import { getTeamUuid } from '../../utils/cookies';

function TextTranscriptionUploadModal({ handleClose, projects = null }) {
    const navigate = useNavigate();
    const { project_uuid } = useParams();
    const [open, setOpen] = useState(true);
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [language, setLanguage] = useState("pt");
    const [text, setText] = useState('');
    const [editedText, setEditedText] = useState('');
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const [project, setProject] = useState(project_uuid ? project_uuid : projects ? projects[0].uuid : null)

    const closeModal = () => {
        handleClose();
        setOpen(false);
    };

    const handleInputChange = (e) => {
        if (e.target.name === 'name') {
            setName(e.target.value);
        } else if (e.target.name === 'text') {
            setText(e.target.value);
        } else if (e.target.name === 'editedText') {
            setEditedText(e.target.value);
        }
    };

    const handleSelectChange = (event) => {
        setLanguage(event.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (step === 1) {
            setStep(2);
        } else if (step === 2) {
            const lines = text.split('\n');
            let parsedText = "";
            let currentSpeaker = null;
            let expectDialogueNext = false;
            let speakerCounter = 0;
            const speakerMap = new Map();

            lines.forEach((line, index) => {
                const trimmedLine = line.trim();
                
                // Check for the new format: "Speaker  H:MM:SS-H:MM:SS" 
                // Use a more specific pattern that captures the speaker name properly
                const timestampPattern = /^(.+?)\s{2}(\d{1,2}:\d{2}:\d{2}-\d{1,2}:\d{2}:\d{2})$/;
                const timestampMatch = trimmedLine.match(timestampPattern);
                
                if (timestampMatch) {
                    // Found a speaker with timestamp
                    const originalSpeaker = timestampMatch[1].trim();
                    // timestampMatch[2] now contains the full timestamp "H:MM:SS-H:MM:SS"
                    
                    // Map speaker names to "Speaker A", "Speaker B" format expected by the system
                    if (!speakerMap.has(originalSpeaker)) {
                        const speakerLetter = String.fromCharCode(65 + speakerCounter); // A, B, C, etc.
                        speakerMap.set(originalSpeaker, `Speaker ${speakerLetter}`);
                        speakerCounter++;
                    }
                    currentSpeaker = speakerMap.get(originalSpeaker);
                    expectDialogueNext = true;
                } else if (trimmedLine && expectDialogueNext && currentSpeaker) {
                    // This is the dialogue for the current speaker - use simple format that backend expects
                    parsedText += `${currentSpeaker}: ${trimmedLine}\n`;
                    expectDialogueNext = false;
                } else if (!trimmedLine) {
                    // Empty line - add paragraph break
                    parsedText += `\n`;
                    expectDialogueNext = false;
                }
            });

            setEditedText(parsedText.trim());
            setStep(3);
        } else if (step === 3) {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('project_uuid', project ? project : project_uuid);
            if (language) { formData.append('language', language) };
            formData.append('team_uuid', getTeamUuid());
            formData.append('edited_transcription', editedText);
            // Extract unique speaker names from the edited text
            const uniqueNames = new Set();
            editedText.split('\n').forEach(line => {
                const trimmedLine = line.trim();
                const speakerMatch = trimmedLine.match(/^(.+?):/);
                if (speakerMatch) {
                    uniqueNames.add(speakerMatch[1].trim());
                }
            });
            formData.append('speakers', Array.from(uniqueNames).join(', '));
            transcriptionAnalysis(formData).then((res) => {
                if (project_uuid) { window.location.reload(false) } else {
                    navigate("/project/" + project)
                }
            }).catch((e) => {

            })
        }
    };



    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => closeModal()}>
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex w-full min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-5xl sm:p-6">
                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center">
                                {t('TranscriptionUploadModal.Upload a transcription')}
                            </Dialog.Title>
                            {step === 1 && (
                                <form className='flex flex-col gap-4' onSubmit={handleSubmit}>
                                    <div>
                                        <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('TranscriptionUploadModal.Name')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={name}
                                            onChange={handleInputChange}
                                            className="block w-full  text-start rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-black focus:ring-2 focus:ring-inset  sm:text-sm sm:leading-6"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('TranscriptionUploadModal.Language')}
                                        </label>
                                        <select
                                            id="location"
                                            name="location"
                                            className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-black sm:text-sm sm:leading-6"
                                            defaultValue={t('Audio.AudioUpload.Step2.LanguageOption1')}
                                            onChange={handleSelectChange}
                                        >
                                            <option>  {t('Audio.AudioUpload.Step2.LanguageOption1')}</option>
                                            <option>  {t('Audio.AudioUpload.Step2.LanguageOption2')}</option>
                                            <option>  {t('Audio.AudioUpload.Step2.LanguageOption3')}</option>
                                        </select>
                                    </div>
                                    {projects && <div>
                                        <label htmlFor="projects" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Audio.AudioUpload.Step2.ProjectLabel')}
                                        </label>
                                        < select
                                            value={project}
                                            onChange={(e) => setProject(e.target.value)}
                                            className="text-black rounded px-4 py-1 text-sm w-full"
                                        >
                                            {projects && (projects?.map((project) => {
                                                return <option key={project.uuid} value={project.uuid}>{project.name}</option>
                                            }))
                                            }
                                        </select>
                                    </div>}
                                    <div className='flex flex-col sm:flex-row gap-2 w-full justify-between items-center mt-8'>
                                        <button
                                            type='button'
                                            onClick={() => { handleClose() }}
                                            className='border  text-black rounded-md px-12 py-2 w-fit'>
                                            {t('Audio.AudioUpload.Step2.BackButton')}
                                        </button>
                                        <button
                                            type='submit'
                                            className='bg-black text-white rounded-md px-12 py-2 w-fit'>
                                            {t('Audio.AudioUpload.Step2.NextButton')}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {step === 2 && (
                                <form className='flex flex-col py-4' onSubmit={handleSubmit}>
                                    <div className='text-sm mb-4'>
                                        {t('TranscriptionUploadModal.Make sure your transcription is formated like the following')}:
                                        <div className='flex flex-col gap-2 mt-2'>
                                            <div className='bg-gray-200 rounded-md px-4 py-2 text-sm font-mono'>
                                                Mike  0:00:00-0:01:09
                                                <br />
                                                {t('TranscriptionUploadModal.Hi')}
                                                <br /><br />
                                                Speaker B  0:01:09-0:01:13
                                                <br />
                                                {t('TranscriptionUploadModal.Hello! How are you')}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('TranscriptionUploadModal.Paste transcription')}:
                                        </label>
                                        <textarea
                                            name="text"

                                            value={text}
                                            onChange={handleInputChange}
                                            placeholder={"Mike  0:00:00-0:01:09\n" + t('TranscriptionUploadModal.Hi') + "\n\nSpeaker B  0:01:09-0:01:13\n" + t('TranscriptionUploadModal.Hello! How are you')}
                                            className="block w-full  text-start rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-black focus:ring-2 focus:ring-inset  sm:text-sm sm:leading-6"
                                            required
                                            rows={15}
                                        />
                                    </div>
                                    <div className='flex flex-col sm:flex-row gap-2 w-full justify-between items-center mt-8'>
                                        <button
                                            type='button'
                                            onClick={() => { if (step == 1) { handleClose() } else { setStep(step - 1) } }}
                                            className='border  text-black rounded-md px-12 py-2 w-fit'>
                                            {t('Audio.AudioUpload.Step2.BackButton')}
                                        </button>
                                        <button
                                            type='submit'
                                            className='bg-black text-white rounded-md px-12 py-2 w-fit'>
                                            {t('Audio.AudioUpload.Step2.NextButton')}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {step === 3 && (
                                <form className='flex flex-col' onSubmit={handleSubmit}>
                                    <div>
                                        <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('TranscriptionUploadModal.Review your transcription')}
                                        </label>
                                        <textarea
                                            name="editedText"
                                            className="block w-full  text-start rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-black focus:ring-2 focus:ring-inset  sm:text-sm sm:leading-6"
                                            value={editedText}
                                            onChange={handleInputChange}
                                            rows={15}
                                            disabled
                                            required
                                        />
                                    </div>
                                    <div className='flex flex-col sm:flex-row gap-2 w-full justify-between items-center mt-8'>
                                        <button
                                            type='button'
                                            onClick={() => { setStep(step - 1) }}
                                            className='border  text-black rounded-md px-12 py-2 w-fit'>
                                            {t('Audio.AudioUpload.Step2.BackButton')}
                                        </button>
                                        <button
                                            type='submit'
                                            className='bg-black text-white rounded-md px-12 py-2 w-fit'>
                                            {t('TranscriptionUploadModal.Upload')}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {loading && <div className="flex justify-center"><ParticleLoader size={24} /></div>}
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default TextTranscriptionUploadModal;
